import { z } from "zod";
import { CalendarDate, Instant, Integer, LocalTime, ProviderId, SiteId, VehicleType, strictExtension } from "./common.js";
// --- Tarifarios, etapa LQ-2 (ADR-026 §2.2, §2.3, §2.4 y §7) ---------------------
// Este modulo y settlement.ts son los UNICOS del wire con dinero (regla 7
// desde LQ-2): montos como enteros de la unidad menor con moneda ISO 4217,
// una sola definicion (Money). La guarda de dinero (money-guard.test.ts)
// falla si un campo de dinero aparece en cualquier otro schema. Solo la
// consola de UCORP escribe tarifas (decision 6); la planta y el proveedor las
// leen desde LQ-4 y LQ-5.
// --- Dinero ------------------------------------------------------------------------
export const CurrencyCode = z
    .string()
    .regex(/^[A-Z]{3}$/, "moneda ISO 4217 de tres letras mayusculas")
    .openapi("CurrencyCode", { description: "Moneda ISO 4217 (ARS, USD). Una por par planta y proveedor; sin conversion.", example: "ARS" });
// zod 4 ya limita .int() al rango seguro, pero no lo publica: los limites
// explicitos mantienen minimum y maximum en el OpenAPI (frontera con mobile).
export const AmountMinor = z
    .number()
    .int()
    .min(Number.MIN_SAFE_INTEGER)
    .max(Number.MAX_SAFE_INTEGER)
    .openapi("AmountMinor", {
    format: "int64",
    description: "Monto entero en la unidad menor de la moneda (centavos), nunca punto flotante. 125000 en ARS son $ 1.250,00.",
});
export const Money = z
    .object({ amountMinor: AmountMinor, currency: CurrencyCode })
    .strict()
    .openapi("Money", { description: "Unica definicion de monto del wire (ADR-026 §7): entero de la unidad menor mas moneda." });
// --- Catalogos -----------------------------------------------------------------------
export const ChargeConcept = z
    .enum(["per_trip", "per_km", "per_hour", "wait", "per_passenger", "minimum", "night_surcharge", "holiday_surcharge", "exception_surcharge", "cancellation"])
    .openapi("ChargeConcept", {
    description: "Concepto de cobro (catalogo cerrado, ADR-026 §2.3): per_trip (salida), per_km, per_hour (disposicion en bloques), wait (espera sobre la tolerancia), per_passenger, minimum (completa la diferencia), night_surcharge, holiday_surcharge, exception_surcharge y cancellation (cancelacion tardia de la planta). Agregar uno es codigo con test, no configuracion.",
});
export const DistanceBasis = z.enum(["planned", "actual"]).openapi("DistanceBasis", {
    description: "Kilometros que cobra la linea: planned (recorrido que armo el sistema, decision 13) o actual (GPS). De un recorrido de Google Routes no hay planificados (R3).",
});
export const DurationBasis = z.enum(["planned", "actual"]).openapi("DurationBasis", {
    description: "Duracion de la disposicion: entre primera y ultima parada planificadas (planned) o reales (actual).",
});
export const PassengerBasis = z.enum(["boarded", "assigned"]).openapi("PassengerBasis", {
    description: "A quien cuenta per_passenger: boarded (a bordo) o assigned (esperados, con ausentes). Lo decide el contrato.",
});
export const SurchargeBase = z.enum(["per_trip", "per_km", "per_trip_and_km"]).openapi("SurchargeBase", {
    description: "Sobre que se aplica un recargo porcentual: la salida, los kilometros o la suma de las dos. Nunca sobre otro recargo.",
});
export const ExceptionCondition = z.enum(["off_shift", "point_to_point", "relaunch"]).openapi("ExceptionCondition", {
    description: "Condicion del recargo por excepcion: off_shift (pedido fuera de turno), point_to_point (auto punto a punto) o relaunch (cola relanzada por la planta). short_notice llega cuando los hechos sellen la antelacion.",
});
export const HourBlockMinutes = z.union([z.literal(15), z.literal(30), z.literal(60)]).openapi("HourBlockMinutes", {
    description: "Tamano del bloque de la disposicion por hora, en minutos: 15, 30 o 60.",
});
export const RateCardVersionStatus = z.enum(["draft", "published"]).openapi("RateCardVersionStatus", {
    description: "draft se edita; published es inmutable y rige en su vigencia. Un cambio de precio es una version nueva.",
});
// --- Condiciones de linea, discriminadas por concepto ----------------------------------
export const FixedSurcharge = z
    .object({ kind: z.literal("fixed") })
    .strict()
    .openapi("FixedSurcharge", { description: "Monto fijo: el precio unitario de la linea." });
export const PercentSurcharge = z
    .object({
    kind: z.literal("percent"),
    basisPoints: Integer.min(1).max(100_000).openapi({ description: "Porcentaje en puntos basicos (2000 = 20,00 %)." }),
    base: SurchargeBase,
})
    .strict()
    .openapi("PercentSurcharge", { description: "Porcentaje sobre una base declarada. La linea lleva precio unitario cero." });
export const SurchargeAmount = z
    .discriminatedUnion("kind", [FixedSurcharge, PercentSurcharge])
    .openapi("SurchargeAmount", { description: "Como se cobra un recargo o una cancelacion: fijo o porcentual." });
const ExclusiveGroup = z.string().trim().min(1).max(40).nullable().openapi({
    description: "Lineas con el mismo grupo no se suman: aplica la mayor (el contrato que dice 'el mayor de los dos'). null: se suma.",
});
const Meters = Integer.min(0).openapi({ description: "Metros (milesimos de kilometro), entero." });
export const PerTripConditions = z.object({ concept: z.literal("per_trip") }).strict()
    .openapi("PerTripConditions", { description: "Salida: 1 por viaje realizado." });
export const PerKmConditions = z
    .object({
    concept: z.literal("per_km"),
    basis: DistanceBasis,
    fromMeters: Meters.openapi({ description: "Tramo desde (inclusive). 0 para una tarifa sin tramos." }),
    toMeters: Meters.min(1).nullable().openapi({ description: "Tramo hasta (exclusivo); null es sin tope. Un tramo por linea: 'de 0 a 30 km' y 'de 30 en adelante' son dos lineas." }),
})
    .strict()
    .openapi("PerKmConditions", { description: "Por kilometro: precio unitario por km, cantidad en metros." });
export const PerHourConditions = z
    .object({
    concept: z.literal("per_hour"),
    basis: DurationBasis,
    blockMinutes: HourBlockMinutes,
    minimumBlocks: Integer.min(0).max(1000).openapi({ description: "Bloques minimos que se cobran aunque el viaje dure menos." }),
})
    .strict()
    .openapi("PerHourConditions", { description: "Disposicion: horas entre primera y ultima parada, en bloques hacia arriba." });
export const WaitConditions = z
    .object({
    concept: z.literal("wait"),
    toleranceMinutes: Integer.min(0).max(1440).openapi({ description: "Minutos de espera que no se cobran: con tolerancia 10, el minuto 10 no se cobra y el 11 si." }),
    blockMinutes: Integer.min(1).max(1440).openapi({ description: "Bloque de cobro de la espera por encima de la tolerancia, hacia arriba." }),
})
    .strict()
    .openapi("WaitConditions", { description: "Espera: minutos por encima de la tolerancia, en bloques." });
export const PerPassengerConditions = z.object({ concept: z.literal("per_passenger"), counts: PassengerBasis }).strict()
    .openapi("PerPassengerConditions", { description: "Por pasajero: a bordo o esperados." });
export const MinimumConditions = z.object({ concept: z.literal("minimum") }).strict()
    .openapi("MinimumConditions", { description: "Minimo del viaje: si la suma de los demas conceptos es menor, la linea completa la diferencia." });
export const NightSurchargeConditions = z
    .object({
    concept: z.literal("night_surcharge"),
    amount: SurchargeAmount,
    from: LocalTime.openapi({ description: "Inicio de la franja en hora local de la planta (inclusive)." }),
    to: LocalTime.openapi({ description: "Fin de la franja (exclusivo); cruza la medianoche si es menor o igual que el inicio." }),
    exclusiveGroup: ExclusiveGroup,
})
    .strict()
    .openapi("NightSurchargeConditions", { description: "Nocturno: aplica si la salida planificada cae en la franja, medida en la zona de la planta." });
export const HolidaySurchargeConditions = z
    .object({
    concept: z.literal("holiday_surcharge"),
    amount: SurchargeAmount,
    includeSundays: z.boolean().openapi({ description: "Los domingos cuentan como feriado si el contrato lo dice." }),
    exclusiveGroup: ExclusiveGroup,
})
    .strict()
    .openapi("HolidaySurchargeConditions", { description: "Feriado: por los feriados de la planta, mas domingos si se pide." });
export const ExceptionSurchargeConditions = z
    .object({ concept: z.literal("exception_surcharge"), amount: SurchargeAmount, condition: ExceptionCondition, exclusiveGroup: ExclusiveGroup })
    .strict()
    .openapi("ExceptionSurchargeConditions", { description: "Recargo por excepcion del catalogo cerrado." });
export const CancellationConditions = z
    .object({
    concept: z.literal("cancellation"),
    amount: SurchargeAmount,
    minimumNoticeMinutes: Integer.min(0).max(43_200).openapi({ description: "Anticipacion minima en minutos desde la salida planificada: con menos, la planta paga esta linea; con la anticipacion justa, no." }),
})
    .strict()
    .openapi("CancellationConditions", { description: "Cancelacion tardia de la planta sobre un viaje ya asignado (§3.3). El porcentaje se aplica sobre la salida y los kilometros planificados." });
export const RateLineConditions = z
    .discriminatedUnion("concept", [
    PerTripConditions,
    PerKmConditions,
    PerHourConditions,
    WaitConditions,
    PerPassengerConditions,
    MinimumConditions,
    NightSurchargeConditions,
    HolidaySurchargeConditions,
    ExceptionSurchargeConditions,
    CancellationConditions,
])
    .openapi("RateLineConditions", { description: "Condiciones de una linea de tarifa, discriminadas por concept. Se guardan validadas por este schema." });
// --- Lineas, versiones y tarifarios ------------------------------------------------------
export const RateLineId = z.string().min(1).openapi("RateLineId", { description: "Identificador de la linea; las lineas de cobro de un viaje lo citan para explicarse." });
// Ladrillo sin registrar: un minimo sobre el $ref de AmountMinor dejaria una rama allOf sin tipo en el OpenAPI.
const UnitAmountMinor = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).openapi({
    format: "int64",
    description: "Precio unitario en la unidad menor (AmountMinor, no negativo): por viaje, por km, por bloque, por pasajero o el monto fijo. Cero en una linea porcentual.",
});
export const RateLineInput = z
    .object({
    unitAmountMinor: UnitAmountMinor,
    conditions: RateLineConditions,
})
    .strict()
    .openapi("RateLineInput", { description: "Linea de tarifa tal como la carga la consola; el id lo asigna la API." });
export const RateLine = strictExtension(RateLineInput.extend({ id: RateLineId }))
    .openapi("RateLine", { description: "Linea de tarifa: un concepto con su precio unitario y sus condiciones." });
export const RateCardId = z.string().min(1).openapi("RateCardId", { description: "Identificador del tarifario dentro de la planta." });
export const RateCardVersionId = z.string().min(1).openapi("RateCardVersionId", { description: "Identificador de la version dentro de la planta." });
const VersionNote = z.string().trim().min(1).max(300).openapi({
    description: "Nota de UCORP (el acuerdo o indice que respalda la version). Texto libre: sin datos de personas; no se loguea.",
});
export const RateCardVersionSummary = z
    .object({
    id: RateCardVersionId,
    rateCardId: RateCardId,
    version: Integer.positive().openapi({ description: "Numero correlativo dentro del tarifario." }),
    status: RateCardVersionStatus,
    validFrom: CalendarDate.openapi({ description: "Primera fecha operativa (local) en que rige, inclusive." }),
    validTo: CalendarDate.optional().openapi({ description: "Ultima fecha operativa en que rige, inclusive. Ausente: sin fin, hasta que otra version publicada la cierre." }),
    currency: CurrencyCode,
    lineCount: Integer.min(0),
    note: VersionNote.optional(),
    createdAt: Instant,
    createdBy: z.string().min(1).openapi({ description: "Identidad verificada de quien la creo." }),
    updatedAt: Instant,
    publishedAt: Instant.optional(),
    publishedBy: z.string().min(1).optional(),
})
    .strict()
    .openapi("RateCardVersionSummary", { description: "Version sin sus lineas, para el listado." });
export const RateCardVersion = strictExtension(RateCardVersionSummary.extend({ lines: z.array(RateLine) }))
    .openapi("RateCardVersion", { description: "Version con sus lineas. Publicada, no se edita nunca (trigger): un cambio de precio es una version nueva." });
export const RateCard = z
    .object({
    id: RateCardId,
    siteId: SiteId,
    providerId: ProviderId,
    vehicleType: VehicleType,
    versions: z.array(RateCardVersionSummary).openapi({ description: "Todas las versiones, de la mas nueva a la mas vieja." }),
    createdAt: Instant,
    createdBy: z.string().min(1),
})
    .strict()
    .openapi("RateCard", { description: "Tarifario de un par planta y proveedor para un tipo de servicio (uno por tipo, decision 9)." });
export const RateCardListResponse = z
    .object({ siteId: SiteId, items: z.array(RateCard) })
    .strict()
    .openapi("RateCardListResponse", { description: "Tarifarios de la planta, de todos sus proveedores (consola)." });
// --- Pedidos -------------------------------------------------------------------------------
export const CreateRateCardRequest = z
    .object({ providerId: ProviderId, vehicleType: VehicleType })
    .strict()
    .openapi("CreateRateCardRequest", { description: "Crea el tarifario del par y tipo de servicio, sin versiones. Auditado." });
export const RATE_LINES_MAX = 50;
export const RateCardVersionInput = z
    .object({
    validFrom: CalendarDate,
    validTo: CalendarDate.optional(),
    note: VersionNote.optional(),
    lines: z.array(RateLineInput).max(RATE_LINES_MAX).openapi({ description: "Lineas en el orden del contrato; para publicar hace falta al menos una." }),
})
    .strict()
    .openapi("RateCardVersionInput", { description: "Contenido de un borrador: vigencia, nota y lineas. La moneda sale de los terminos del par." });
export const DeriveRateCardVersionRequest = z
    .object({
    percentBasisPoints: Integer.min(-9999).max(100_000).openapi({ description: "Ajuste de los precios unitarios en puntos basicos (1800 = +18,00 %; -500 = -5,00 %). Las lineas porcentuales no cambian." }),
    validFrom: CalendarDate,
    validTo: CalendarDate.optional(),
    note: VersionNote.optional(),
})
    .strict()
    .openapi("DeriveRateCardVersionRequest", { description: "Nueva version a partir de otra, mas N % (ADR-026 §2.2): crea un borrador con todas las lineas ajustadas y el mismo redondeo; un humano lo revisa y lo publica." });
export const PublishRateCardVersionRequest = z
    .object({ confirm: z.literal(true).openapi({ description: "Confirmacion explicita: publicar deja la version inmutable." }) })
    .strict()
    .openapi("PublishRateCardVersionRequest", { description: "Publica un borrador." });
export const RateCardSiteParams = z.object({ siteId: SiteId }).strict().openapi("RateCardSiteParams", { description: "Planta de los tarifarios." });
export const RateCardParams = z.object({ siteId: SiteId, rateCardId: RateCardId }).strict().openapi("RateCardParams", { description: "Planta y tarifario." });
export const RateCardVersionParams = z
    .object({ siteId: SiteId, rateCardId: RateCardId, versionId: RateCardVersionId })
    .strict()
    .openapi("RateCardVersionParams", { description: "Planta, tarifario y version." });
// --- Rutas ------------------------------------------------------------------------------------
// Bloque propio (API_ROUTES lo incluye). Todas de la consola: compartimento
// ucorp; lee ucorp_admin_reader, escribe solo ucorp_admin.
const AUTH = [400, 401, 403];
const ADMIN = { tag: "admin", security: "bearer_admin" };
const WRITE = "Rol ucorp_admin (editor). Auditado con antes y despues. 409 para el tenant interno.";
export const RATE_CARD_ROUTES = [
    { method: "GET", path: "/admin/tenants/{siteId}/rate-cards", operationId: "adminRateCards", summary: "Tarifarios de la planta", description: "Compartimento ucorp, rol ucorp_admin o ucorp_admin_reader. Todos los proveedores de la planta con sus versiones (sin lineas). 404 si la planta no existe.", ...ADMIN, params: RateCardSiteParams, response: { status: 200, schema: RateCardListResponse, description: "Tarifarios." }, errors: [...AUTH, 404] },
    { method: "POST", path: "/admin/tenants/{siteId}/rate-cards", operationId: "adminCreateRateCard", summary: "Crear tarifario", description: `${WRITE} 404 si el proveedor no es de la planta; 400 si no presta ese tipo de servicio; 409 rate_card_exists si el par ya tiene tarifario para ese tipo.`, ...ADMIN, params: RateCardSiteParams, body: CreateRateCardRequest, response: { status: 201, schema: RateCard, description: "Tarifario creado, sin versiones." }, errors: [...AUTH, 404, 409] },
    { method: "GET", path: "/admin/tenants/{siteId}/rate-cards/{rateCardId}/versions/{versionId}", operationId: "adminRateCardVersion", summary: "Version de un tarifario con sus lineas", description: "Compartimento ucorp, rol ucorp_admin o ucorp_admin_reader. 404 si la version no es de ese tarifario y esa planta.", ...ADMIN, params: RateCardVersionParams, response: { status: 200, schema: RateCardVersion, description: "Version con lineas." }, errors: [...AUTH, 404] },
    { method: "POST", path: "/admin/tenants/{siteId}/rate-cards/{rateCardId}/versions", operationId: "adminCreateRateCardVersion", summary: "Crear un borrador de version", description: `${WRITE} Moneda de los terminos del par. 400 si una linea no tiene sentido (porcentaje con precio, tramo invertido, fechas invertidas).`, ...ADMIN, params: RateCardParams, body: RateCardVersionInput, response: { status: 201, schema: RateCardVersion, description: "Borrador creado." }, errors: [...AUTH, 404, 409] },
    { method: "POST", path: "/admin/tenants/{siteId}/rate-cards/{rateCardId}/versions/{versionId}/derive", operationId: "adminDeriveRateCardVersion", summary: "Nueva version a partir de otra, mas N %", description: `${WRITE} Borrador con las lineas de la version dada ajustadas por el porcentaje, redondeo mitad al par a la unidad menor; las lineas porcentuales no cambian.`, ...ADMIN, params: RateCardVersionParams, body: DeriveRateCardVersionRequest, response: { status: 201, schema: RateCardVersion, description: "Borrador derivado." }, errors: [...AUTH, 404, 409] },
    { method: "PUT", path: "/admin/tenants/{siteId}/rate-cards/{rateCardId}/versions/{versionId}", operationId: "adminUpdateRateCardVersion", summary: "Editar un borrador", description: `${WRITE} Reemplaza vigencia, nota y lineas. 409 rate_card_version_published si la version ya se publico.`, ...ADMIN, params: RateCardVersionParams, body: RateCardVersionInput, response: { status: 200, schema: RateCardVersion, description: "Borrador actualizado." }, errors: [...AUTH, 404, 409] },
    { method: "POST", path: "/admin/tenants/{siteId}/rate-cards/{rateCardId}/versions/{versionId}/publish", operationId: "adminPublishRateCardVersion", summary: "Publicar una version", description: `${WRITE} Exige confirm true y al menos una linea. Cierra la version publicada anterior sin fin el dia previo al inicio de esta. 409 rate_card_version_overlap si pisa la vigencia de otra publicada; 409 rate_card_version_published si ya estaba publicada; 409 conflict si la moneda ya no es la de los terminos del par.`, ...ADMIN, params: RateCardVersionParams, body: PublishRateCardVersionRequest, response: { status: 200, schema: RateCardVersion, description: "Version publicada, inmutable." }, errors: [...AUTH, 404, 409] },
    { method: "DELETE", path: "/admin/tenants/{siteId}/rate-cards/{rateCardId}/versions/{versionId}", operationId: "adminDeleteRateCardVersion", summary: "Descartar un borrador", description: `${WRITE} Solo borradores: 409 rate_card_version_published si la version se publico.`, ...ADMIN, params: RateCardVersionParams, response: { status: 200, schema: RateCard, description: "Tarifario sin el borrador." }, errors: [...AUTH, 404, 409] },
];
//# sourceMappingURL=rate-cards.js.map